<template>
  <div class="presentation-container">
    <app-loading :visible="loading"></app-loading>

    <div v-if="!loading && !error" class="slides">
      <!-- Slide 1: Capa -->
      <section class="slide slide-cover">
        <div class="cover-content">
          <div class="cover-header">
            <span class="cover-date">{{ currentDate }}</span>
            <h2 class="cover-celebration-name">{{ celebration.name }}</h2>
          </div>
          <div class="cover-center">
            <img src="@/assets/logo-heppi.png" class="cover-logo" />
            <h1 class="cover-title">Acompanhamento<br>financeiro periódico</h1>
          </div>
        </div>
      </section>

      <!-- Slide 2: Visão Geral -->
      <section class="slide slide-overview">
        <div class="slide-header">
          <div class="slide-header-left">
            <span class="slide-section-label">{{ celebration.name }}</span>
            <h2 class="slide-title">Visão geral</h2>
          </div>
          <div class="slide-header-right">
            <img src="@/assets/logo-heppi.png" class="slide-logo" />
            <span class="slide-date">{{ currentDate }}</span>
          </div>
        </div>
        <div class="overview-content">
          <div class="overview-left">
            <div class="overview-metric">
              <v-icon color="grey">mdi-flag-outline</v-icon>
              <div>
                <small>Orçamento de</small>
                <div class="metric-value">{{ celebration.goal | price }}</div>
              </div>
            </div>
            <div class="overview-metric">
              <v-icon color="grey">mdi-account-group-outline</v-icon>
              <div>
                <small>{{ totals.totalParticipants }}/{{ totals.totalParticipants }}</small>
                <div class="metric-label">participantes</div>
              </div>
            </div>
            <div class="overview-metric">
              <v-icon color="grey">mdi-calendar-outline</v-icon>
              <div>
                <small>{{ celebration.event_date | moment('MMMM YYYY') }}</small>
                <div class="metric-label">{{ monthsUntilEvent }}</div>
              </div>
            </div>
          </div>
          <div class="overview-center">
            <div class="donut-chart-container">
              <div class="donut-chart" :style="donutChartStyle">
                <div class="donut-hole"></div>
              </div>
            </div>
            <div class="chart-legend-vertical">
              <div class="legend-row legend-paid-row">
                <span class="legend-color-box legend-paid"></span>
                <span class="legend-label">Pago</span>
                <span class="legend-value success--text">{{ totals.totalPaid | price }}</span>
              </div>
              <div class="legend-row legend-late-row">
                <span class="legend-color-box legend-late"></span>
                <span class="legend-label">Atrasado</span>
                <span class="legend-value error--text">{{ totals.totalLate | price }}</span>
              </div>
              <div class="legend-row legend-pending-row">
                <span class="legend-color-box legend-pending"></span>
                <span class="legend-label">Previsto</span>
                <span class="legend-value grey--text">{{ totals.totalPending | price }}</span>
              </div>
            </div>
          </div>
          <div class="overview-right">
            <div class="overview-indicator">
              <v-icon color="grey">mdi-currency-usd</v-icon>
              <div>
                <small>Média arrecadada</small>
                <div class="indicator-value" :class="averageStatus.class">
                  {{ averageStatus.label }} <v-icon small :color="averageStatus.color">{{ averageStatus.icon }}</v-icon>
                </div>
              </div>
            </div>
            <div class="overview-indicator">
              <v-icon color="grey">mdi-alert-circle-outline</v-icon>
              <div>
                <small>Inadimplência</small>
                <div class="indicator-value" :class="delinquencyStatus.class">
                  {{ delinquencyStatus.label }} <v-icon small :color="delinquencyStatus.color">{{ delinquencyStatus.icon }}</v-icon>
                </div>
              </div>
            </div>
            <div class="overview-indicator">
              <v-icon color="grey">mdi-gift-outline</v-icon>
              <div>
                <small>Recompensa Heppi</small>
                <div class="indicator-value reward">{{ totals.totalCashback | price }}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Slide 3: Valores Pagos (Entradas) -->
      <section class="slide slide-income">
        <div class="slide-header">
          <div class="slide-header-left">
            <span class="slide-section-label">{{ celebration.name }}</span>
            <h2 class="slide-title"><v-icon class="mr-2 income-icon">mdi-currency-usd</v-icon>Valores pagos</h2>
          </div>
          <div class="slide-header-right">
            <img src="@/assets/logo-heppi.png" class="slide-logo" />
            <span class="slide-date">{{ currentDate }}</span>
          </div>
        </div>
        <div class="income-content">
          <div class="income-left">
            <h3 class="income-section-title">Arrecadações</h3>
            <div class="income-total success--text">{{ totals.totalPaidFundraisings | price }}</div>
            <div class="fundraisings-list">
              <div v-for="fundraising in fundraisings" :key="fundraising.id" class="fundraising-item">
                <div class="fundraising-progress">
                  <div class="fundraising-bar" :style="{ width: getFundraisingProgress(fundraising) + '%' }"></div>
                </div>
                <div class="fundraising-info">
                  <span class="fundraising-name">{{ fundraising.name }}</span>
                  <span class="fundraising-value success--text">{{ fundraising.paid | price }}</span>
                  <small class="fundraising-meta">{{ getFundraisingProgress(fundraising) }}% | {{ fundraising.amount_to_pay | price }}*</small>
                </div>
              </div>
            </div>
          </div>
          <div class="income-right">
            <div class="income-box">
              <div class="income-box-total">{{ totals.totalIncome | price }}</div>
            </div>
            <div class="income-details">
              <div class="income-detail-item">
                <small>Compensação por atraso</small>
                <span>{{ totals.totalInterest | price }}</span>
              </div>
              <div class="income-detail-item">
                <small>Desistentes<br><small>(valor remanescente)</small></small>
                <span>{{ totals.totalWaivers | price }}</span>
              </div>
              <div class="income-detail-item">
                <small>Outros créditos**</small>
                <span>{{ totals.totalCredits | price }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="slide-footer">
          <small>* Percentual da meta previsto caso todo mundo continue arrecadando e não entre mais ninguém na turma</small>
          <small>** Valor transferido de outras experiências ou migrações</small>
        </div>
      </section>

      <!-- Slide 4: Inadimplência -->
      <section class="slide slide-delinquency">
        <div class="slide-header">
          <div class="slide-header-left">
            <span class="slide-section-label">{{ celebration.name }}</span>
            <h2 class="slide-title"><v-icon color="warning" class="mr-2">mdi-alert-circle-outline</v-icon>Inadimplência</h2>
          </div>
          <div class="slide-header-right">
            <img src="@/assets/logo-heppi.png" class="slide-logo" />
            <span class="slide-date">{{ currentDate }}</span>
          </div>
        </div>
        <div class="delinquency-content">
          <div class="delinquency-left">
            <v-chip :color="delinquencyStatus.chipColor" class="delinquency-chip">
              Inadimplência {{ delinquencyStatus.label }}
            </v-chip>
            <div class="delinquency-metrics">
              <div class="delinquency-metric">
                <v-icon color="grey">mdi-account-group</v-icon>
                <div class="metric-content">
                  <span class="metric-big">{{ delinquency.count }}<small>/{{ totals.totalParticipants }}</small></span>
                  <span class="metric-description">inadimplentes</span>
                </div>
              </div>
              <div class="delinquency-metric">
                <v-icon color="warning">mdi-alert</v-icon>
                <div class="metric-content">
                  <span class="metric-big">{{ delinquency.belowMinimum }}<small>/{{ totals.totalParticipants }}</small></span>
                  <span class="metric-description">abaixo do valor mínimo *</span>
                </div>
              </div>
              <div class="delinquency-metric">
                <v-icon color="grey">mdi-snowflake</v-icon>
                <div class="metric-content">
                  <span class="metric-big">{{ delinquency.frozen }}<small>/{{ totals.totalParticipants }}</small></span>
                  <span class="metric-description">congelados</span>
                </div>
              </div>
            </div>
          </div>
          <div class="delinquency-center">
            <h4>Atrasados</h4>
            <div class="delinquency-total error--text">{{ totals.totalLate | price }}</div>
            <div class="delinquency-by-fundraising">
              <div v-for="fundraising in fundraisings" :key="'late-' + fundraising.id" class="delinquency-fundraising-item">
                <span class="fundraising-name">{{ fundraising.name }}</span>
                <div>
                  <span class="error--text">{{ fundraising.late | price }}</span>
                  <small class="ml-2">{{ getFundraisingLatePercent(fundraising) }}%</small>
                </div>
              </div>
            </div>
          </div>
          <div class="delinquency-right">
            <h4>Renegociações em aberto</h4>
            <div class="delinquency-total warning--text">{{ totals.totalRenegotiations | price }}</div>
            <div class="delinquency-by-fundraising">
              <div v-for="fundraising in fundraisings" :key="'reneg-' + fundraising.id" class="delinquency-fundraising-item">
                <span class="fundraising-name">{{ fundraising.name }}</span>
                <div>
                  <span class="warning--text">{{ (fundraising.renegotiations || 0) | price }}</span>
                  <small class="ml-2">{{ getFundraisingRenegPercent(fundraising) }}%</small>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="slide-footer">
          <small>* As pessoas abaixo do valor mínimo são as pessoas que ainda não pagaram o suficiente comparado com outras pessoas da turma no mesmo momento da arrecadação e já tem mais de 3 meses no processo. Mas não necessariamente são inadimplentes. São pontos de atenção.</small>
        </div>
      </section>

      <!-- Slide 5: Transferências e Pagamentos (Saídas) -->
      <section class="slide slide-outcomes">
        <div class="slide-header">
          <div class="slide-header-left">
            <span class="slide-section-label">{{ celebration.name }}</span>
            <h2 class="slide-title"><v-icon color="error" class="mr-2">mdi-bank-transfer-out</v-icon>Transferências e pagamentos</h2>
          </div>
          <div class="slide-header-right">
            <img src="@/assets/logo-heppi.png" class="slide-logo" />
            <span class="slide-date">{{ currentDate }}</span>
          </div>
        </div>
        <div class="outcomes-content">
          <div class="outcomes-left">
            <h4>Pagamento de<br><strong>Fornecedores</strong></h4>
            <div class="outcomes-total"><span class="currency">R$</span> <span class="value">{{ totals.totalSupplierPayments | priceNumber }}</span></div>
            <div class="donut-chart-container outcomes-chart">
              <div class="donut-chart" :style="outcomesChartStyle">
                <div class="donut-hole"></div>
              </div>
            </div>
            <div class="suppliers-list">
              <div v-for="(supplier, index) in supplierPayments" :key="index" class="supplier-item">
                <span class="supplier-color" :style="{ backgroundColor: supplierColors[index] }"></span>
                <span class="supplier-name">{{ supplier.name }}</span>
                <span class="supplier-value">{{ supplier.amount | price }}</span>
              </div>
            </div>
          </div>
          <div class="outcomes-right">
            <div class="outcomes-box">
              <div class="outcomes-box-total">{{ totals.totalOutcomes | price }}</div>
            </div>
            <div class="outcomes-details">
              <div class="outcomes-detail-item">
                <small>Devoluções p/ desistência</small>
                <span>{{ totals.totalDevolutions | price }}</span>
              </div>
              <div class="outcomes-detail-item">
                <small>Resgate para a conta da turma</small>
                <span>{{ totals.totalWithdraws | price }}</span>
              </div>
              <div class="outcomes-detail-item">
                <small>Plano Heppi</small>
                <span>{{ totals.totalHeppiPlan | price }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Slide 6: Recompensa Heppi -->
      <section class="slide slide-rewards">
        <div class="slide-header">
          <div class="slide-header-left">
            <span class="slide-section-label">{{ celebration.name }}</span>
            <h2 class="slide-title"><v-icon color="primary" class="mr-2">mdi-chart-line</v-icon>Recompensa Heppi</h2>
          </div>
          <div class="slide-header-right">
            <img src="@/assets/logo-heppi.png" class="slide-logo" />
            <span class="slide-date">{{ currentDate }}</span>
          </div>
        </div>
        <div class="rewards-content">
          <div class="rewards-left">
            <h4>Últimos 6 meses</h4>
            <div class="chart-legend-rewards">
              <span class="legend-item"><span class="legend-color legend-balance"></span> Saldo da turma</span>
              <span class="legend-item"><span class="legend-color legend-accumulated"></span> Recompensa acumulada</span>
              <span class="legend-item"><span class="legend-color legend-month"></span> Ganho do mês</span>
            </div>
            <div class="bar-chart-container rewards-chart">
              <div class="bar-chart">
                <div v-for="(data, index) in rewards.monthlyData" :key="index" class="bar-group">
                  <div class="bar-stack">
                    <div class="bar bar-balance" :style="{ height: getBarHeight(data.balance) + 'px' }"></div>
                    <div class="bar bar-accumulated" :style="{ height: getBarHeight(data.accumulated) + 'px' }"></div>
                    <div class="bar bar-gain" :style="{ height: getBarHeight(data.gain) + 'px' }">
                      <span v-if="data.gain > 0" class="bar-value">{{ data.gain | priceShort }}</span>
                    </div>
                  </div>
                  <span class="bar-label">{{ data.month }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="rewards-right">
            <div class="rewards-box">
              <div class="rewards-box-total">{{ totals.totalCashback | price }}</div>
            </div>
            <div class="rewards-details">
              <div class="rewards-detail-item">
                <small>Resgatado + IR Resgate</small>
                <span>{{ rewards.withdrawn | price }}</span>
              </div>
              <div class="rewards-detail-item">
                <small>Simulação IR</small>
                <span>{{ rewards.taxSimulation | price }}</span>
              </div>
              <div class="rewards-detail-item highlight">
                <small>Valor líquido atual</small>
                <span>{{ rewards.netValue | price }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Slide 7: Arrecadação Facilitada (condicional) -->
      <section v-if="facilitatedFundraising" class="slide slide-facilitated">
        <div class="slide-header">
          <div class="slide-header-left">
            <span class="slide-section-label">{{ celebration.name }}</span>
            <h2 class="slide-title"><v-icon color="primary" class="mr-2">mdi-hand-coin-outline</v-icon>Arrecadação facilitada</h2>
          </div>
          <div class="slide-header-right">
            <img src="@/assets/logo-heppi.png" class="slide-logo" />
            <span class="slide-date">{{ currentDate }}</span>
          </div>
        </div>
        <div class="facilitated-content">
          <div class="facilitated-metrics">
            <div class="facilitated-metric">
              <v-icon color="grey">mdi-account-group</v-icon>
              <div>
                <span class="metric-big">{{ facilitatedFundraising.filledSlots }}<small>/{{ facilitatedFundraising.totalSlots }}</small></span>
                <small>vagas preenchidas</small>
              </div>
            </div>
            <div class="facilitated-metric">
              <v-icon color="success">mdi-currency-usd</v-icon>
              <div>
                <span class="metric-value success--text">{{ facilitatedFundraising.paid | price }}</span>
                <small>pago</small>
              </div>
            </div>
            <div class="facilitated-metric">
              <v-icon color="grey">mdi-flag-outline</v-icon>
              <div>
                <span class="metric-value">{{ facilitatedFundraising.expected | price }}</span>
                <small>previsto</small>
              </div>
            </div>
          </div>
          <div class="facilitated-details">
            <div class="facilitated-left">
              <v-chip :color="facilitatedFundraising.delinquencyStatus.chipColor" class="delinquency-chip">
                Inadimplência {{ facilitatedFundraising.delinquencyStatus.label }}
              </v-chip>
              <div class="facilitated-delinquency-metrics">
                <div class="facilitated-delinquency-metric">
                  <v-icon color="grey" small>mdi-account-group</v-icon>
                  <span class="metric-small">{{ facilitatedFundraising.delinquents }}<small>/{{ facilitatedFundraising.filledSlots }}</small></span>
                  <small>inadimplentes</small>
                </div>
                <div class="facilitated-delinquency-metric">
                  <v-icon color="error" small>mdi-currency-usd</v-icon>
                  <span class="metric-small error--text">{{ facilitatedFundraising.lateAmount | price }}</span>
                  <small>atrasado</small>
                </div>
              </div>
            </div>
            <div class="facilitated-right">
              <div class="facilitated-cta">
                <h4>Novas adesões</h4>
                <div class="qr-placeholder">
                  <v-icon size="100" color="grey lighten-1">mdi-qrcode</v-icon>
                </div>
                <div class="facilitated-cta-info">
                  <div class="cta-metric">
                    <v-icon color="grey" small>mdi-account-group</v-icon>
                    <span><strong>{{ facilitatedFundraising.availableSlots }}</strong> vagas disponíveis</span>
                  </div>
                  <div class="cta-metric">
                    <v-icon color="grey" small>mdi-calendar</v-icon>
                    <span><strong>50%</strong> pago após a formatura</span>
                  </div>
                </div>
                <small class="cta-link">Mais informações em:<br><strong>heppi.com.br/facilita</strong></small>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- Erro -->
    <div v-if="error" class="error-container">
      <v-icon size="64" color="error">mdi-alert-circle-outline</v-icon>
      <h3>Houve um erro ao carregar os dados</h3>
      <p>Por favor, tente novamente</p>
      <v-btn color="primary" @click="loadData">Tentar novamente</v-btn>
    </div>

    <!-- Botão de imprimir -->
    <div v-if="!loading && !error" class="print-actions no-print">
      <v-btn color="primary" large @click="printPresentation">
        <v-icon left>mdi-printer</v-icon>
        Imprimir / Salvar PDF
      </v-btn>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters, mapMutations, mapActions } from 'vuex'
import AppLoading from '@/components/Loading'

export default {
  name: 'CelebrationPresentation',
  components: {
    AppLoading
  },
  props: ['celebrationId'],
  data: () => ({
    loading: true,
    error: false,
    celebration: {
      name: '',
      event_date: '',
      goal: 0,
      gateway_id: null
    },
    fundraisings: [],
    totals: {
      totalParticipants: 0,
      totalPaid: 0,
      totalPaidFundraisings: 0,
      totalLate: 0,
      totalPending: 0,
      totalIncome: 0,
      totalInterest: 0,
      totalWaivers: 0,
      totalCredits: 0,
      totalCashback: 0,
      totalOutcomes: 0,
      totalSupplierPayments: 0,
      totalDevolutions: 0,
      totalWithdraws: 0,
      totalHeppiPlan: 0,
      totalRenegotiations: 0
    },
    delinquency: {
      count: 0,
      belowMinimum: 0,
      frozen: 0
    },
    supplierPayments: [],
    supplierColors: [
      '#6B7B9B', // Primeiro
      '#909CB4', // Segundo
      '#B5BDCD', // Terceiro
      '#DADEE6' // Quarto
    ],
    maxBarValue: 1000,
    rewards: {
      withdrawn: 0,
      taxSimulation: 0,
      netValue: 0,
      monthlyData: []
    },
    facilitatedFundraising: null
  }),
  computed: {
    ...mapGetters(['transfers', 'withdraws', 'heppiPlan', 'cashback']),
    currentDate () {
      return this.$moment().format('DD MMMM YYYY')
    },
    monthsUntilEvent () {
      if (!this.celebration.event_date) return ''
      const eventDate = this.$moment(this.celebration.event_date)
      const now = this.$moment()
      const months = eventDate.diff(now, 'months')
      if (months <= 0) return 'Evento realizado'
      return `+${months} meses`
    },
    averageStatus () {
      // Lógica para calcular se está acima ou abaixo da média
      // Por enquanto retorna um valor padrão
      return {
        label: 'Acima da média',
        class: 'status-good',
        color: 'success',
        icon: 'mdi-check-circle'
      }
    },
    delinquencyStatus () {
      const percent = this.totals.totalParticipants > 0
        ? (this.delinquency.count / this.totals.totalParticipants) * 100
        : 0

      if (percent <= 10) {
        return { label: 'Baixa', class: 'status-good', color: 'success', icon: 'mdi-check-circle', chipColor: 'success' }
      } else if (percent <= 30) {
        return { label: 'Moderada', class: 'status-warning', color: 'warning', icon: 'mdi-alert', chipColor: 'warning' }
      } else {
        return { label: 'Alta', class: 'status-bad', color: 'error', icon: 'mdi-alert-circle', chipColor: 'error' }
      }
    },
    donutChartStyle () {
      const total = this.totals.totalPaid + this.totals.totalLate + this.totals.totalPending
      if (total === 0) return { background: '#BDBDBD' }

      const paidPercent = (this.totals.totalPaid / total) * 100
      const latePercent = (this.totals.totalLate / total) * 100

      // Conic gradient: Pago (verde), Atrasado (vermelho), Previsto (cinza)
      return {
        background: `conic-gradient(
          #04544C 0% ${paidPercent}%,
          #E94351 ${paidPercent}% ${paidPercent + latePercent}%,
          #BDBDBD ${paidPercent + latePercent}% 100%
        )`
      }
    },
    outcomesChartStyle () {
      if (this.supplierPayments.length === 0) return { background: '#E0E0E0' }

      const total = this.supplierPayments.reduce((sum, s) => sum + s.amount, 0)
      if (total === 0) return { background: '#E0E0E0' }

      let gradientParts = []
      let currentPercent = 0

      this.supplierPayments.forEach((supplier, index) => {
        const percent = (supplier.amount / total) * 100
        const color = this.supplierColors[index] || '#DADEE6'
        gradientParts.push(`${color} ${currentPercent}% ${currentPercent + percent}%`)
        currentPercent += percent
      })

      return {
        background: `conic-gradient(${gradientParts.join(', ')})`
      }
    }
  },
  methods: {
    ...mapMutations(['SET_SNACKBAR']),
    ...mapActions(['fetchTransfers']),

    async loadData () {
      this.loading = true
      this.error = false

      try {
        // 1. Buscar dados da celebração
        const celebrationResponse = await axios.get(`celebrations/${this.celebrationId}`)
        if (celebrationResponse.data) {
          this.celebration = {
            name: celebrationResponse.data.name || '',
            event_date: celebrationResponse.data.event_date || '',
            goal: celebrationResponse.data.goal || 0,
            gateway_id: celebrationResponse.data.gateway_id || null
          }
        }

        // 2. Buscar transações da conta via N8N (todas as categorias)
        if (this.celebration.gateway_id) {
          const categories = ['transfer', 'request_withdraw', 'heppi_plan', 'cashback', 'facilitated_fee']
          await Promise.all(categories.map(cat =>
            this.fetchTransfers({ accountId: this.celebration.gateway_id, category: cat })
          ))
        }

        // 3. Buscar arrecadações
        const fundraisingsResponse = await axios.get(`fundraisings?filter[celebration_id]=${this.celebrationId}`)
        if (fundraisingsResponse.data && fundraisingsResponse.data.data) {
          this.fundraisings = fundraisingsResponse.data.data.map(f => ({
            id: f.id,
            name: f.name,
            amount_to_pay: 0,
            paid: 0,
            paid_before: 0,
            paid_without_interest_and_fine: 0,
            paid_interest_and_fine: 0,
            late: 0,
            pending: 0,
            participants: 0,
            renegotiations: 0
          }))
        }

        // 3. Buscar detalhes de cada arrecadação
        for (let i = 0; i < this.fundraisings.length; i++) {
          const fundraisingDetail = await axios.get(`fundraisings/${this.fundraisings[i].id}`)
          if (fundraisingDetail.data && fundraisingDetail.data.participants) {
            let amountToPay = 0
            let paidBefore = 0
            let paidWithoutInterestAndFine = 0
            let paidInterestAndFine = 0
            let late = 0
            let pending = 0
            let delinquents = 0

            for (const participant of fundraisingDetail.data.participants) {
              amountToPay += parseFloat(participant.amount_to_pay) || 0
              paidBefore += Math.max(0, parseFloat(participant.total_paid_before) || 0)
              paidWithoutInterestAndFine += parseFloat(participant.total_paid_without_interest_and_fine) || 0
              paidInterestAndFine += parseFloat(participant.total_paid_interest_and_fine) || 0
              late += parseFloat(participant.total_late) || 0

              const participantPending = (parseFloat(participant.amount_to_pay) || 0) -
                (parseFloat(participant.total_paid_before) || 0) -
                (parseFloat(participant.total_paid_without_interest_and_fine) || 0) -
                (parseFloat(participant.total_late) || 0)
              pending += Math.max(0, participantPending)

              if ((parseFloat(participant.total_late) || 0) > 0) {
                delinquents++
              }
            }

            this.fundraisings[i] = {
              ...this.fundraisings[i],
              amountToPay,
              paidBefore,
              paidWithoutInterestAndFine,
              paidInterestAndFine,
              paid: paidBefore + paidWithoutInterestAndFine,
              late,
              pending,
              participants: fundraisingDetail.data.participants.length,
              delinquents
            }

            // Atualizar totais
            this.totals.totalParticipants += fundraisingDetail.data.participants.length
            this.totals.totalPaid += paidBefore + paidWithoutInterestAndFine
            this.totals.totalPaidFundraisings += paidBefore + paidWithoutInterestAndFine
            this.totals.totalLate += late
            this.totals.totalPending += pending
            this.totals.totalInterest += paidInterestAndFine
            this.delinquency.count += delinquents
          }
        }

        // 4. Buscar dados da conta (se existir gateway_id)
        if (this.celebration.gateway_id) {
          await this.loadAccountData()
        }

        // 5. Calcular totais finais
        this.calculateTotals()

        // 6. Calcular valor máximo para gráfico de barras
        this.calculateMaxBarValue()

        this.loading = false
      } catch (err) {
        console.error('Erro ao carregar dados:', err)
        this.error = true
        this.loading = false
      }
    },

    async loadAccountData () {
      try {
        // Buscar saldo da conta
        const accountResponse = await axios.get(
          `accounts/${this.celebration.gateway_id}?api_key=${this.celebration.gateway_id}`,
          { baseURL: 'https://payment-gateway-api.heppi.io/api/' }
        )

        if (accountResponse.data) {
          this.totals.availableBalance = accountResponse.data.available_balance || 0
          this.totals.unavailableBalance = accountResponse.data.unavailable_balance || 0
        }

        // Processar transferências do Vuex
        const accountTransfers = this.transfers(this.celebration.gateway_id) || []
        const accountWithdraws = this.withdraws(this.celebration.gateway_id) || []
        const accountHeppiPlan = this.heppiPlan(this.celebration.gateway_id) || []
        const accountCashback = this.cashback(this.celebration.gateway_id) || []

        // Calcular totais de saídas
        let supplierMap = {}

        accountTransfers.forEach(t => {
          if (t.financial_type === 'debit') {
            const providerName = t.receiver?.name || 'Outros'
            if (!supplierMap[providerName]) {
              supplierMap[providerName] = 0
            }
            supplierMap[providerName] += parseFloat(t.amount) || 0
            this.totals.totalSupplierPayments += parseFloat(t.amount) || 0

            // Verificar se é devolução por desistência
            if (t.description && t.description.toLowerCase().includes('desist')) {
              this.totals.totalDevolutions += parseFloat(t.amount) || 0
            }
          } else if (t.financial_type === 'credit') {
            this.totals.totalCredits += parseFloat(t.amount) || 0
          }
        })

        // Converter mapa de fornecedores para array
        this.supplierPayments = Object.entries(supplierMap)
          .map(([name, amount]) => ({ name, amount }))
          .sort((a, b) => b.amount - a.amount)
          .slice(0, 4) // Top 4 fornecedores

        // Withdraws
        accountWithdraws.forEach(w => {
          this.totals.totalWithdraws += parseFloat(w.amount) || 0
        })

        // Heppi Plan
        accountHeppiPlan.forEach(h => {
          this.totals.totalHeppiPlan += parseFloat(h.amount) || 0
        })

        // Cashback
        accountCashback.forEach(c => {
          this.totals.totalCashback += parseFloat(c.amount) || 0
        })

        // Processar dados mensais de recompensa para o gráfico
        this.processRewardsMonthlyData(accountCashback)
      } catch (err) {
        console.error('Erro ao carregar dados da conta:', err)
      }
    },

    processRewardsMonthlyData (cashbackData) {
      const monthlyMap = {}
      const now = this.$moment()

      // Inicializar últimos 6 meses
      for (let i = 5; i >= 0; i--) {
        const month = now.clone().subtract(i, 'months').format('MMM YY')
        monthlyMap[month] = { balance: 0, accumulated: 0, gain: 0 }
      }

      // Agregar cashback por mês
      cashbackData.forEach(c => {
        const month = this.$moment(c.payment_date?.$date).format('MMM YY')
        if (monthlyMap[month]) {
          monthlyMap[month].gain += parseFloat(c.amount) || 0
        }
      })

      // Calcular acumulado
      let accumulated = 0
      this.rewards.monthlyData = Object.entries(monthlyMap).map(([month, data]) => {
        accumulated += data.gain
        return {
          month,
          balance: data.balance,
          accumulated,
          gain: data.gain
        }
      })

      // Calcular valores de recompensa
      this.rewards.netValue = this.totals.totalCashback
      this.rewards.taxSimulation = this.totals.totalCashback * 0.15 // Simulação de 15% IR
    },

    calculateTotals () {
      this.totals.totalIncome = this.totals.totalPaidFundraisings +
        this.totals.totalInterest +
        this.totals.totalCredits

      this.totals.totalOutcomes = this.totals.totalSupplierPayments +
        this.totals.totalDevolutions +
        this.totals.totalWithdraws +
        this.totals.totalHeppiPlan
    },

    calculateMaxBarValue () {
      if (this.rewards.monthlyData.length > 0) {
        const maxValue = Math.max(...this.rewards.monthlyData.map(d =>
          d.balance + d.accumulated + d.gain
        ))
        this.maxBarValue = maxValue > 0 ? maxValue : 1000
      }
    },

    getBarHeight (value) {
      const maxHeight = 150 // altura máxima em pixels
      if (this.maxBarValue === 0) return 0
      return Math.max(2, (value / this.maxBarValue) * maxHeight)
    },

    getFundraisingProgress (fundraising) {
      if (!fundraising.amount_to_pay || fundraising.amount_to_pay === 0) return 0
      return Math.round((fundraising.paid / fundraising.amount_to_pay) * 100)
    },

    getFundraisingLatePercent (fundraising) {
      if (!fundraising.amount_to_pay || fundraising.amount_to_pay === 0) return 0
      return Math.round((fundraising.late / fundraising.amount_to_pay) * 100)
    },

    getFundraisingRenegPercent (fundraising) {
      if (!fundraising.amount_to_pay || fundraising.amount_to_pay === 0) return 0
      return Math.round(((fundraising.renegotiations || 0) / fundraising.amount_to_pay) * 100)
    },

    printPresentation () {
      window.print()
    }
  },
  filters: {
    priceShort (value) {
      if (!value && value !== 0) return ''
      const num = parseFloat(value)
      if (num >= 1000) {
        return 'R$ ' + (num / 1000).toFixed(1).replace('.', ',') + 'k'
      }
      return 'R$ ' + num.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    },
    priceNumber (value) {
      if (!value && value !== 0) return '0,00'
      const num = parseFloat(value)
      return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }
  },
  created () {
    if (this.celebrationId) {
      this.loadData()
    } else {
      this.error = true
      this.loading = false
    }
  }
}
</script>

<style lang="scss">
// Import Open Sauce font
@import url('https://fonts.cdnfonts.com/css/open-sauce-one');

// Variáveis
$primary-color: #3D1C46;
$success-color: #04544C;
$error-color: #E94351;
$warning-color: #E0AD36;
$grey-color: #757575;
$font-family: 'Open Sauce One', 'Open Sauce', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;

.presentation-container {
  background: #f5f5f5;
  min-height: 100vh;
  font-family: $font-family;

  // Aplicar fonte em elementos de texto (exceto ícones)
  h1, h2, h3, h4, h5, h6,
  p, span:not(.v-icon):not(.mdi):not([class*="mdi-"]),
  small, strong, div, label, a,
  .v-chip, .v-btn {
    font-family: $font-family;
  }
}

// Slides Container
.slides {
  max-width: 960px;
  margin: 0 auto;
  padding: 20px;
}

// Base Slide
.slide {
  background: white;
  border-radius: 8px;
  margin-bottom: 20px;
  padding: 40px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  min-height: 540px;
  position: relative;
}

// Slide Header
.slide-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
}

.slide-header-left {
  .slide-section-label {
    font-size: 12px;
    color: $primary-color;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-family: 'Open Sauce One', 'Open Sauce', sans-serif;
  }

  .slide-title {
    font-size: 28px;
    color: $primary-color;
    margin: 5px 0 0 0;
    display: flex;
    align-items: center;
    font-family: 'Open Sauce One', 'Open Sauce', sans-serif;
  }
}

.slide-header-right {
  text-align: right;

  .slide-logo {
    height: 40px;
    margin-bottom: 5px;
  }

  .slide-date {
    display: block;
    font-size: 12px;
    color: $grey-color;
  }
}

// Slide Cover
.slide-cover {
  background: #410E47;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;

  .cover-content {
    text-align: center;
    width: 100%;
  }

  .cover-header {
    position: absolute;
    top: 40px;
    right: 40px;
    text-align: right;

    .cover-date {
      font-size: 14px;
      opacity: 0.8;
    }

    .cover-celebration-name {
      font-size: 18px;
      font-weight: bold;
      margin-top: 5px;
    }
  }

  .cover-center {
    .cover-logo {
      height: 80px;
      margin-bottom: 30px;
      filter: brightness(0) invert(1);
    }

    .cover-title {
      font-size: 42px;
      font-weight: 300;
      line-height: 1.2;
    }
  }
}

// Slide Overview
.overview-content {
  display: grid;
  grid-template-columns: 1fr 2fr 1fr;
  gap: 30px;
  align-items: center;
}

.overview-metric, .overview-indicator {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 20px;

  small {
    font-size: 17px;
  }

  .metric-value {
    font-size: 24px;
    font-weight: bold;
    white-space: nowrap;
    color: rgba(64, 64, 64, 0.87);
    font-family: 'Open Sauce One', 'Open Sauce', sans-serif;
  }

  .metric-label {
    font-size: 18px;
    color: $grey-color;
  }

  .indicator-value {
    font-size: 16px;
    font-weight: bold;

    &.status-good { color: $success-color; }
    &.status-warning { color: $warning-color; }
    &.status-bad { color: $error-color; }
    &.reward { color: $primary-color; }
  }
}

// Donut Chart CSS
.donut-chart-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
}

.donut-chart {
  position: relative;
  width: 180px;
  height: 180px;
  border-radius: 50%;

  .donut-hole {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 120px;
    height: 120px;
    background: white;
    border-radius: 50%;
  }
}

// Bar Chart CSS
.bar-chart-container {
  height: 200px;
  display: flex;
  align-items: flex-end;
}

.bar-chart {
  display: flex;
  justify-content: space-around;
  align-items: flex-end;
  width: 100%;
  height: 100%;
  padding-bottom: 30px;
}

.bar-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  max-width: 60px;
}

.bar-stack {
  display: flex;
  flex-direction: column-reverse;
  align-items: center;
  width: 40px;
}

.bar {
  width: 100%;
  border-radius: 4px 4px 0 0;
  position: relative;

  &.bar-balance {
    background: #E0E0E0;
  }

  &.bar-accumulated {
    background: #CE93D8;
  }

  &.bar-gain {
    background: #7B1FA2;
  }

  .bar-value {
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 10px;
    color: $grey-color;
    white-space: nowrap;
  }
}

.bar-label {
  margin-top: 8px;
  font-size: 11px;
  color: $grey-color;
  text-transform: uppercase;
}

// Legenda Vertical do Gráfico
.chart-legend-vertical {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
  padding: 0 20px;

  .legend-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    background: #FAFAFA;
    border-radius: 8px;
    border-left: 4px solid transparent;

    &.legend-paid-row {
      border-left-color: $success-color;
    }

    &.legend-late-row {
      border-left-color: $error-color;
    }

    &.legend-pending-row {
      border-left-color: #BDBDBD;
    }
  }

  .legend-color-box {
    width: 16px;
    height: 16px;
    border-radius: 4px;
    flex-shrink: 0;

    &.legend-paid { background: $success-color; }
    &.legend-late { background: $error-color; }
    &.legend-pending { background: #BDBDBD; }
  }

  .legend-label {
    font-size: 14px;
    color: $grey-color;
    flex: 1;
  }

  .legend-value {
    font-size: 16px;
    font-weight: bold;
    text-align: right;
  }
}

// Legenda horizontal (para outros gráficos)
.chart-legend {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 15px;

  .legend-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    text-align: center;
  }

  .legend-color {
    width: 12px;
    height: 12px;
    border-radius: 2px;

    &.legend-paid { background: $success-color; }
    &.legend-late { background: $error-color; }
    &.legend-pending { background: #BDBDBD; }
    &.legend-balance { background: #E0E0E0; }
    &.legend-accumulated { background: #CE93D8; }
    &.legend-month { background: #7B1FA2; }
  }
}

// Slide Income
.income-icon {
  color: #9F86BB !important;
}

.income-content {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 40px;
}

.income-section-title {
  color: $grey-color;
  font-size: 14px;
  margin-bottom: 5px;
}

.income-total {
  font-size: 32px;
  font-weight: bold;
  margin-bottom: 20px;
}

.fundraisings-list {
  .fundraising-item {
    margin-bottom: 15px;
  }

  .fundraising-progress {
    height: 8px;
    background: #E0E0E0;
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 5px;

    .fundraising-bar {
      height: 100%;
      background: $success-color;
      border-radius: 4px;
    }
  }

  .fundraising-info {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    flex-wrap: wrap;

    .fundraising-name {
      font-weight: 500;
    }

    .fundraising-value {
      font-weight: bold;
    }

    .fundraising-meta {
      width: 100%;
      color: $grey-color;
      font-size: 11px;
    }
  }
}

.income-box, .outcomes-box, .rewards-box {
  background: #F5F5F5;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  margin-bottom: 20px;

  &-total {
    font-size: 28px;
    font-weight: bold;
    color: $primary-color;
  }
}

.income-box {
  background: #04544C;

  .income-box-total {
    color: white;
  }
}

.outcomes-box {
  background: #6B7B9B;
  font-size: 15px;

  .outcomes-box-total {
    color: white;
  }
}

.income-details, .outcomes-details, .rewards-details {
  .income-detail-item, .outcomes-detail-item, .rewards-detail-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px solid #eee;

    small {
      color: $grey-color;
    }

    span {
      font-weight: 500;
      font-size: 15px;
      text-align: right;
    }

    &.highlight {
      background: #F5F5F5;
      margin: 10px -10px 0;
      padding: 15px 10px;
      border-radius: 8px;
      border: none;
    }
  }
}

// Slide Delinquency
.delinquency-content {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 30px;
}

.delinquency-chip {
  margin-bottom: 20px;
}

.delinquency-metrics {
  .delinquency-metric {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 18px;

    .metric-content {
      display: flex;
      flex-direction: column;
    }

    .metric-big {
      font-size: 26px;
      font-weight: bold;
      line-height: 1.2;

      small {
        font-size: 16px;
        color: $grey-color;
        font-weight: normal;
      }
    }

    .metric-description {
      display: block;
      font-size: 13px;
      color: $grey-color;
      margin-top: 2px;
    }
  }
}

h4 {
  color: rgba(100, 100, 100, 0.87);
  text-align: left;
}

.delinquency-total {
  font-size: 28px;
  font-weight: bold;
  margin-bottom: 15px;
}

.delinquency-by-fundraising {
  .delinquency-fundraising-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
    border-bottom: 1px solid #eee;
    min-height: 48px;
    gap: 8px;

    .fundraising-name {
      font-size: 13px;
      flex: 1;
      min-width: 0;
      word-break: break-word;
    }

    > div {
      display: flex;
      align-items: center;
      white-space: nowrap;
      flex-shrink: 0;
    }
  }
}

// Slide Outcomes
.outcomes-content {
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 40px;
}

.outcomes-total {
  font-size: 28px;
  font-weight: bold;
  margin: 10px 0 20px;

  .currency {
    color: #999999;
    font-size: 17px;
    font-weight: 300;
    height: 31px;
    display: inline-block;
    vertical-align: middle;
  }

  .value {
    color: #6B7B9B;
    margin-left: 5px;
    margin-right: 5px;
  }
}

.outcomes-chart {
  height: 150px;
  margin-bottom: 20px;

  .donut-chart {
    width: 140px;
    height: 140px;

    .donut-hole {
      width: 80px;
      height: 80px;
    }
  }
}

.suppliers-list {
  .supplier-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 0;

    .supplier-color {
      width: 12px;
      height: 12px;
      border-radius: 2px;
    }

    .supplier-name {
      flex: 1;
      font-size: 13px;
    }

    .supplier-value {
      font-weight: 500;
    }
  }
}

// Slide Rewards
.rewards-content {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 40px;
}

.chart-legend-rewards {
  display: flex;
  gap: 20px;
  margin-bottom: 15px;
  font-size: 11px;
}

.rewards-chart {
  height: 250px;
}

// Slide Facilitated
.facilitated-content {
  .facilitated-metrics {
    display: flex;
    justify-content: space-around;
    padding: 20px 0;
    border-bottom: 1px solid #eee;
    margin-bottom: 30px;

    .facilitated-metric {
      display: flex;
      align-items: center;
      gap: 12px;

      .metric-big {
        font-size: 28px;
        font-weight: bold;

        small {
          font-size: 16px;
          color: $grey-color;
        }
      }

      .metric-value {
        font-size: 24px;
        font-weight: bold;
      }
    }
  }

  .facilitated-details {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 40px;
  }

  .facilitated-cta {
    background: #F5F5F5;
    border-radius: 8px;
    padding: 20px;
    text-align: center;

    h4 {
      background: $primary-color;
      color: white;
      padding: 8px 20px;
      border-radius: 20px;
      display: inline-block;
      margin-bottom: 15px;
    }

    .qr-placeholder {
      margin: 15px 0;
    }

    .cta-metric {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      margin: 8px 0;
      font-size: 13px;
    }

    .cta-link {
      display: block;
      margin-top: 15px;
      color: $grey-color;
    }
  }
}

// Slide Footer
.slide-footer {
  position: absolute;
  bottom: 20px;
  left: 40px;
  right: 40px;

  small {
    display: block;
    font-size: 10px;
    color: $grey-color;
    line-height: 1.4;
  }
}

// Error Container
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  text-align: center;

  h3 {
    margin: 20px 0 10px;
  }

  p {
    color: $grey-color;
    margin-bottom: 20px;
  }
}

// Print Actions
.print-actions {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 100;
}

// Print Styles
@media print {
  .no-print {
    display: none !important;
  }

  .presentation-container {
    background: white;
  }

  .slides {
    max-width: none;
    padding: 0;
  }

  .slide {
    page-break-after: always;
    page-break-inside: avoid;
    box-shadow: none;
    border-radius: 0;
    margin: 0;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  // Gui. comentei aqui porque tava alterando os outros documentos que não eram horizontais
  // @page {
  //   size: A4 landscape;
  //   margin: 0;
  // }
}
</style>
